import { lazy } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

// project-imports
import ErrorBoundary from './ErrorBoundary';
import Loadable from 'components/Loadable';
import DashboardLayout from 'layout/Dashboard';
import AuthGuard from 'utils/route-guard/AuthGuard';

// Vektra screens
const Home = Loadable(lazy(() => import('pages/vektra/home')));
const Analytics = Loadable(lazy(() => import('pages/vektra/analytics')));
const WorkOrdersList = Loadable(lazy(() => import('pages/vektra/work-orders/list')));
const WorkOrderDetail = Loadable(lazy(() => import('pages/vektra/work-orders/detail')));
const WorkOrderCreate = Loadable(lazy(() => import('pages/vektra/work-orders/create')));
const WorkOrderOperatorTasks = Loadable(lazy(() => import('pages/vektra/work-orders/operator')));
const WorkOrderLineExecution = Loadable(lazy(() => import('pages/vektra/work-orders/operator/line')));
const VehiclesList = Loadable(lazy(() => import('pages/vektra/vehicles/list')));
const VehicleDetail = Loadable(lazy(() => import('pages/vektra/vehicles/detail')));
const VehicleForm = Loadable(lazy(() => import('pages/vektra/vehicles/form')));
const CustomersList = Loadable(lazy(() => import('pages/vektra/customers/list')));
const CustomerDetail = Loadable(lazy(() => import('pages/vektra/customers/detail')));
const CustomerForm = Loadable(lazy(() => import('pages/vektra/customers/form')));
const UsersList = Loadable(lazy(() => import('pages/vektra/users/list')));
const UserDetail = Loadable(lazy(() => import('pages/vektra/users/detail')));
const UserForm = Loadable(lazy(() => import('pages/vektra/users/form')));
const Profile = Loadable(lazy(() => import('pages/vektra/profile')));
const ProfileEdit = Loadable(lazy(() => import('pages/vektra/profile/edit')));
const BillingSoon = Loadable(lazy(() => import('pages/vektra/billing-soon')));
const Notifications = Loadable(lazy(() => import('pages/vektra/notifications')));
const WarehouseForm = Loadable(lazy(() => import('pages/vektra/warehouse/create')));
const WarehousesList = Loadable(lazy(() => import('pages/vektra/warehouse/list')));
const BillingConfigList = Loadable(lazy(() => import('pages/vektra/billing-config/list')));
const BillingConfigDetail = Loadable(lazy(() => import('pages/vektra/billing-config/detail')));
const BillingConfigEdit = Loadable(lazy(() => import('pages/vektra/billing-config/edit')));
const PaymentDetailsPage = Loadable(lazy(() => import('pages/vektra/payment-details')));
const OperationalScheduleEdit = Loadable(lazy(() => import('pages/vektra/operational-schedule/edit')));
const InvoiceDetail = Loadable(lazy(() => import('pages/vektra/invoice/detail')));
const MaintenanceList = Loadable(lazy(() => import('pages/vektra/maintenance/list')));
const WorkLogsList = Loadable(lazy(() => import('pages/vektra/work-logs/list')));
const OperatorMonthPage = Loadable(lazy(() => import('pages/vektra/work-logs/operator-month')));
const PayRatesPage = Loadable(lazy(() => import('pages/vektra/pay-rates')));
// SUPPORT-01 (VEK-161) — the account's support requests.
const SupportPage = Loadable(lazy(() => import('pages/vektra/support')));
const MaintenanceDetail = Loadable(lazy(() => import('pages/vektra/maintenance/detail')));
const MaintenanceCreate = Loadable(lazy(() => import('pages/vektra/maintenance/create')));
const MaintenanceConfigPage = Loadable(lazy(() => import('pages/vektra/maintenance-config')));
const VehicleParametersPage = Loadable(lazy(() => import('pages/vektra/vehicle-parameters')));
const InspectionTemplateList = Loadable(lazy(() => import('pages/vektra/inspection-template/list')));
const InspectionTemplateEdit = Loadable(lazy(() => import('pages/vektra/inspection-template/edit')));
const MapPage = Loadable(lazy(() => import('pages/vektra/map')));
const CalendarPage = Loadable(lazy(() => import('pages/vektra/calendar')));
const ScannerPage = Loadable(lazy(() => import('pages/vektra/scanner')));
const SensorsList = Loadable(lazy(() => import('pages/vektra/sensors/list')));

// NAV-01 (VEK-93) — /dashboard is the old home. Old bookmarks, QR hand-offs and external links land on
// /home with their query string intact and no extra history entry.
function RedirectHome() {
  const { search } = useLocation();
  return <Navigate to={{ pathname: '/home', search }} replace />;
}

// ==============================|| MAIN ROUTING ||============================== //

const MainRoutes = {
  path: '/',
  children: [
    {
      path: '/',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        // NAV-01 (VEK-93) — Home is the login landing (empty until VEK-94 fills it); the reporting page is
        // Analytics; /dashboard redirects so nothing that pointed at the old home breaks.
        { path: 'home', element: <Home />, ErrorBoundary },
        { path: 'analytics', element: <Analytics />, ErrorBoundary },
        { path: 'dashboard', element: <RedirectHome />, ErrorBoundary },

        // Work Orders (v2 — docs/features/work-order-management-v2)
        { path: 'work-orders', element: <WorkOrdersList />, ErrorBoundary },
        { path: 'work-orders/create', element: <WorkOrderCreate />, ErrorBoundary },
        // VEK-79 — the same page in estimate mode: the order is created as an ESTIMATE and holds nothing.
        // Declared before `work-orders/:id` so "estimate" is never read as an order id; the key keeps
        // React from carrying the create page's state across when one navigates to the other.
        { path: 'work-orders/estimate', element: <WorkOrderCreate key="estimate" estimateMode />, ErrorBoundary },
        // A DRAFT reopens in the same wizard the create flow uses, hydrated from the server.
        { path: 'work-orders/:id/edit', element: <WorkOrderCreate />, ErrorBoundary },
        { path: 'work-orders/tasks', element: <WorkOrderOperatorTasks />, ErrorBoundary },
        { path: 'work-orders/tasks/:workOrderId/:itemId', element: <WorkOrderLineExecution />, ErrorBoundary },
        { path: 'work-orders/:id', element: <WorkOrderDetail />, ErrorBoundary },

        // Fleet / Vehicles
        { path: 'fleet', element: <VehiclesList />, ErrorBoundary },
        { path: 'vehicle/create', element: <VehicleForm mode="create" />, ErrorBoundary },
        { path: 'vehicle/:id', element: <VehicleDetail />, ErrorBoundary },
        { path: 'vehicle/edit/:id', element: <VehicleForm mode="edit" />, ErrorBoundary },

        // Customers
        { path: 'customers', element: <CustomersList />, ErrorBoundary },
        { path: 'customer/create', element: <CustomerForm mode="create" />, ErrorBoundary },
        { path: 'customer/:id', element: <CustomerDetail />, ErrorBoundary },
        { path: 'customer/edit/:id', element: <CustomerForm mode="edit" />, ErrorBoundary },

        // Users
        { path: 'users', element: <UsersList />, ErrorBoundary },
        { path: 'user/create', element: <UserForm mode="create" />, ErrorBoundary },
        { path: 'user/:id', element: <UserDetail />, ErrorBoundary },
        { path: 'user/edit/:id', element: <UserForm mode="edit" />, ErrorBoundary },

        // Profile / Notifications
        { path: 'profile', element: <Profile />, ErrorBoundary },
        // USER-03 (VEK-256) — the avatar menu's Edit profile and Billing rows
        { path: 'profile/edit', element: <ProfileEdit />, ErrorBoundary },
        { path: 'billing', element: <BillingSoon />, ErrorBoundary },
        { path: 'notifications', element: <Notifications />, ErrorBoundary },

        // Warehouse — WH-01 (VEK-203): the list and the edit route; the create route is unchanged
        { path: 'warehouses', element: <WarehousesList />, ErrorBoundary },
        { path: 'warehouse/create', element: <WarehouseForm />, ErrorBoundary },
        { path: 'warehouse/edit/:id', element: <WarehouseForm />, ErrorBoundary },

        // Billing Config
        { path: 'billing-config', element: <BillingConfigList />, ErrorBoundary },
        { path: 'billing-config/create', element: <BillingConfigEdit mode="create" />, ErrorBoundary },
        { path: 'billing-config/:vehicleType', element: <BillingConfigDetail />, ErrorBoundary },
        { path: 'billing-config/:vehicleType/edit', element: <BillingConfigEdit mode="edit" />, ErrorBoundary },
        { path: 'payment-details', element: <PaymentDetailsPage />, ErrorBoundary },

        // Vehicle parameters (VPARAM-01 — the account's per-type catalog, reached from Fleet)
        { path: 'vehicle-parameters', element: <VehicleParametersPage />, ErrorBoundary },

        // Operational Schedule
        { path: 'operational-schedule', element: <OperationalScheduleEdit />, ErrorBoundary },

        // Invoice
        { path: 'invoice/:id', element: <InvoiceDetail />, ErrorBoundary },

        // Maintenance
        { path: 'maintenance', element: <MaintenanceList />, ErrorBoundary },
        { path: 'maintenance/create', element: <MaintenanceCreate />, ErrorBoundary },
        { path: 'maintenance/:id', element: <MaintenanceDetail />, ErrorBoundary },

        // WORKLOG-03 — the account's work logs, reviewed in one list
        { path: 'work-logs', element: <WorkLogsList />, ErrorBoundary },
        // WORKLOG-08 (VEK-159) — one operator's month, under the same Work hours entry
        { path: 'work-logs/operators/:operatorId', element: <OperatorMonthPage />, ErrorBoundary },
        { path: 'pay-rates', element: <PayRatesPage />, ErrorBoundary },
        { path: 'support', element: <SupportPage />, ErrorBoundary },

        // Maintenance Configuration (PM cycles per vehicle type)
        { path: 'maintenance-config', element: <MaintenanceConfigPage />, ErrorBoundary },
        { path: 'maintenance-config/:vehicleType', element: <MaintenanceConfigPage />, ErrorBoundary },

        // Inspection Templates (CHECK_IN / RETURN_CHECK_IN)
        { path: 'inspection-template', element: <InspectionTemplateList />, ErrorBoundary },
        { path: 'inspection-template/create', element: <InspectionTemplateEdit mode="create" />, ErrorBoundary },
        { path: 'inspection-template/:id', element: <InspectionTemplateEdit mode="edit" />, ErrorBoundary },

        // Map / Calendar / Scanner
        { path: 'map', element: <MapPage />, ErrorBoundary },
        { path: 'calendar', element: <CalendarPage />, ErrorBoundary },
        { path: 'scanner', element: <ScannerPage />, ErrorBoundary },

        // Sensors
        { path: 'sensors', element: <SensorsList />, ErrorBoundary }
      ]
    }
  ]
};

export default MainRoutes;
